/**
 * colegiosService.js
 * Servicio para gestionar colegios
 */

import api from './api';

export const colegiosService = {
  // Listar colegios con paginacion
  // Params: { page, limit, search }
  async listarColegios(params = {}) {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.set('page', params.page);
    if (params.limit) queryParams.set('limit', params.limit);
    if (params.search) queryParams.set('search', params.search);

    const queryString = queryParams.toString();
    return api.get(`/colegios${queryString ? '?' + queryString : ''}`);
  },

  // Obtener un colegio por ID
  async obtenerColegio(id) {
    return api.get(`/colegios/${id}`);
  },

  // Crear nuevo colegio
  async crearColegio(datos) {
    return api.post('/colegios', datos);
  },

  // Crear nueva base de datos para colegio existente
  async crearNuevoAnio(colegioId, anio, copiarDe = null) {
    return api.post(`/colegios/${colegioId}/nuevo-anio`, { anio, copiarDe });
  },

  // Actualizar datos basicos del colegio
  async actualizarColegio(id, datos) {
    return api.put(`/colegios/${id}`, datos);
  },

  // Actualizar datos de personalizacion del colegio
  async actualizarPersonalizacion(id, datos) {
    return api.put(`/colegios/${id}/personalizacion`, datos);
  },

  // Descargar backup de un esquema
  async descargarBackupEsquema(colegioId, nombreEsquema) {
    const { blob, filename } = await api.downloadFile(`/colegios/${colegioId}/esquemas/${nombreEsquema}/backup`);

    // Crear URL temporal y descargar
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || `backup_${nombreEsquema}_${new Date().toISOString().split('T')[0]}.sql`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);

    return { success: true };
  },

  // Restaurar backup de un esquema
  async restaurarBackupEsquema(colegioId, nombreEsquema, archivo, usuarioId, usuarioNombre) {
    const formData = new FormData();
    formData.append('backup', archivo);
    formData.append('usuario_id', usuarioId || '');
    formData.append('usuario_nombre', usuarioNombre || '');

    return api.postFormData(`/colegios/${colegioId}/esquemas/${nombreEsquema}/restore`, formData);
  },

  // Eliminar esquema individual (PELIGROSO - requiere confirmacion)
  async eliminarEsquema(colegioId, nombreEsquema, confirmacion, usuarioId, usuarioNombre) {
    return api.del(`/colegios/${colegioId}/esquemas/${nombreEsquema}`, {
      confirmacion,
      usuario_id: usuarioId,
      usuario_nombre: usuarioNombre
    });
  },

  // Eliminar colegio completamente (PELIGROSO - requiere confirmacion)
  async eliminarColegio(id, confirmacion, usuarioId, usuarioNombre) {
    return api.del(`/colegios/${id}`, {
      confirmacion,
      usuario_id: usuarioId,
      usuario_nombre: usuarioNombre
    });
  },

  // Solicitar token de impersonation para acceder a un colegio como Admin
  async solicitarImpersonation(colegioId, esquema) {
    return api.post('/auth/impersonate', { colegioId, esquema });
  },

  // Solicitar token de impersonation para acceder como usuario SISTEMA (Soporte1/Soporte2)
  async solicitarImpersonationSistema(userId) {
    return api.post('/auth/impersonate-sistema', { userId });
  },

  // Validar token de impersonation y obtener sesión
  // Usa fetch directo (no api.get) porque este endpoint no requiere JWT
  // y api.get redirige al login en caso de 401, causando un loop
  async validarImpersonation(token) {
    const response = await fetch(`${api.API_URL}/auth/impersonate/validate?token=${encodeURIComponent(token)}`);
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || 'Token inválido o expirado');
    }
    return data;
  },

  // Descargar backup masivo de TODOS los colegios (ZIP)
  async descargarBackupMasivo() {
    const { blob, filename } = await api.downloadFile('/colegios/backup-masivo');

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || `Backup_${new Date().toISOString().split('T')[0]}.zip`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);

    return { success: true };
  },

  // Obtener estudiantes activos por colegio (Root plataforma)
  async obtenerEstudiantesPorColegio() {
    return api.get('/dashboard/plataforma/estudiantes-por-colegio');
  },

  // Obtener tamaño de almacenamiento de archivos por esquema
  async obtenerAlmacenamiento() {
    return api.get('/colegios/almacenamiento');
  }
};
